import { React, createContext, useContext, useEffect, useRef, useState } from "react";
import { Drawer } from '@mui/joy';
import { v4 as uuidv4 } from 'uuid';
import { default as Dump } from '../../Dump';
import { AppContext } from "src/mtska-frontend-app-scaffolding"
import { APP } from "src/mtska-frontend-app-scaffolding/constants/constants"
import { getMissingDrawerPermission } from './drawerPermissions';
import DrawerPermissionDenied from './DrawerPermissionDenied';

export const DrawerContext = createContext()

export const DrawerProvider = ({ children, modules }) => {
    const { userCapabilities, existingModules } = useContext(AppContext);
    const [baseRoute, setBaseRoute] = useState([]);

    const [actives, setActives] = useState([]);

    const isInsideDrawer = () => {
        return actives.length > 0
    }

    const activate = (routeName, params, callback = () => { }, buttons) => {
        setActives((prev) => [...prev, {
            routeName: routeName,
            params: params,
            callback: callback,
            uid: uuidv4(),
            buttons: buttons,
        }]);
    }

    const initBaseRoute = (routeName, params, callback = () => { }) => {
        if (actives.length !== 0) return;

        setBaseRoute({
            routeName: routeName,
            params: params,
            callback: callback,
            uid: uuidv4()
        });
    }

    const routeIsInstack = (routeName) => {
        return Array.isArray(actives) && actives.some(e => e.routeName === routeName);
    }

    const hasRoute = (routeName) => {
        return routes[routeName] !== undefined;
    }

    const close = (data) => {
        const stack = [...actives];
        const active = stack.pop();
        setActives([...stack]);

        active && active.callback && active.callback(data);
    }

    const handleClose = (uid, data) => {
        const stack = [...actives];
        const active = stack.find(item => item.uid === uid);
        active.callback && active.callback(data);
        const index = stack.findIndex(item => item.uid === uid);
        if (index > -1) {
            stack.splice(index, 1);
        }
        setActives([...stack]);
    };

    const closeAll = (data) => {
        const stack = [...actives];
        setActives([]);
        while (stack.length > 0) {
            const active = stack.pop();
            active?.callback?.(data);
        }
    };

    const [fullScreen, setFullScreen] = useState(false);
    const toggleFullscreen = () => {
        setFullScreen(!fullScreen);
    }

    const [routes, setRoutes] = useState({});

    useEffect(() => {
        if (!Array.isArray(modules)) return;
        const drawerRoutes = {}
        modules.map((m, k) => {
            if (!Array.isArray(m.drawerRoutes)) return;
            m.drawerRoutes.map((r, j) => {
                const fullPath = `${m.name}/${r.path}`;
                drawerRoutes[fullPath] = r.Component
            })
        });

        //console.log("drawerRoutes", drawerRoutes)
        setRoutes({ ...drawerRoutes });
    }, [modules]);

    return (
        <>
            <DrawerContext.Provider value={{ hasRoute, initBaseRoute, activate, routeIsInstack, close, closeAll, baseRoute, actives, toggleFullscreen, isInsideDrawer }}>
                {children}
                {actives.map((active, i, row) => {
                    const isLast = (i + 1 === row.length);
                    const Component = routes[active.routeName];
                    if (typeof Component === 'undefined') {
                        console.info(`Drawer route with name '${active.routeName} is not registered!'`, routes);
                        alert(`Drawer route with name '${active.routeName} is not registered!'`);
                        return null;
                    }
                    const missingPermission = getMissingDrawerPermission(
                        active.routeName,
                        active.params,
                        userCapabilities,
                        existingModules,
                    );
                    return (
                        <div key={active.uid}>
                            <DrawerComponent uid={active.uid} onClose={handleClose} isLast={isLast} fullScreen={fullScreen} toggleFullscreen={toggleFullscreen} buttons={active.buttons}>
                                {missingPermission ? (
                                    <DrawerPermissionDenied permission={missingPermission} />
                                ) : (
                                    <>
                                        <Dump params={active.params} />
                                        {Component ? (<Component isDrawer uid={active.uid} handleClose={handleClose} {...active.params} />) : (<>No view available</>)}
                                    </>
                                )}
                            </DrawerComponent>
                        </div>
                    )
                })}
            </DrawerContext.Provider>
        </>
    );
};

export const useDrawer = () => {
    const context = useContext(DrawerContext);
    if (context === undefined) {
        throw new Error("useDrawer must be used within a DrawerProvider");
    }

    return {

        activate: context.activate,
        hasRoute: context.hasRoute,
        routeIsInstack: context.routeIsInstack,
        initBaseRoute: context.initBaseRoute,
        baseRoute: context.baseRoute,
        actives: context.actives,
        close: context.close,
        closeAll: context.closeAll,
        toggleFullscreen: context.toggleFullscreen,
        isInsideDrawer: context.isInsideDrawer

    };
};

const DrawerComponent = ({ uid, children, isLast, fullScreen, toggleFullscreen, onClose = () => { }, buttons }) => {

    const { appConfiguration } = useContext(AppContext)

    const { app } = Array.isArray(appConfiguration) ? {} : (appConfiguration || {})
    const { instance } = app || {}

    const [opened, setOpened] = useState(false);
    const isClosingRef = useRef(false);

    // we open after rendering to show transitions
    useEffect(() => {
        setOpened(true);
    }, []);

    // close with transitions
    const handleClose = (_, reason) => {
        isClosingRef.current = true;
        setOpened(false);
    }

    // after "closing transition" -> callback to un-render the component
    const handleTransationEnd = () => {
        if (!isClosingRef.current) return;
        if (opened) return;
        onClose(uid);
    };
    const fullwidth = !isLast || fullScreen;


    return (
        <Drawer
            onTransitionEnd={handleTransationEnd}
            open={opened}
            onClose={handleClose}
            anchor={"right"}
            disableScrollLock
            disableAutoFocus
            className={"drawer-wrapper drawer-wrapper-" + uid + " drawer-wrapper-fullwidth-" + fullwidth}
        >
            {children}
        </Drawer>
    )
}


